import React, { useEffect, useState } from 'react';
import NoActiveProject from '../components/NoActiveProject';
import { tools } from '../tools/catalog';

export default function PlaceholderPage({ title, tool, icon, description, mockStats, mockData }) {
    const toolLabel = tools[tool]?.label || tool;
    const [activeProjectId, setActiveProjectId] = useState(localStorage.getItem('activeProjectId') || '');

    useEffect(() => {
        const sync = () => setActiveProjectId(localStorage.getItem('activeProjectId') || '');
        window.addEventListener('activeProjectChanged', sync);
        return () => window.removeEventListener('activeProjectChanged', sync);
    }, []);

    if (!activeProjectId) {
        return (
            <NoActiveProject
                description={`Select a project in the header to use the ${toolLabel} tool.`}
            />
        );
    }

    return (
        <div style={{ padding: '1rem 1.25rem', maxWidth: '1200px', margin: '0 auto', animation: 'fadeIn 0.3s ease' }}>
            {/* Breadcrumb */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                <span>Tools</span>
                <i className="fa-solid fa-chevron-right" style={{ fontSize: '0.7rem' }}></i>
                <span>{toolLabel}</span>
                <i className="fa-solid fa-chevron-right" style={{ fontSize: '0.7rem' }}></i>
                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{title}</span>
            </div>

            {/* Title Block */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: 'var(--radius)', 
                    background: 'var(--bg-secondary)', 
                    border: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <i className={icon} style={{ fontSize: '1.1rem', color: 'var(--accent)' }}></i>
                </div>
                <div>
                    <h1 style={{ fontSize: '1.35rem', margin: 0, fontWeight: 700 }}>{title}</h1>
                    <p style={{ color: 'var(--text-secondary)', margin: '0.25rem 0 0 0', fontSize: '0.95rem' }}>{description}</p>
                </div>
            </div>

            {/* Mock Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                {mockStats.map((stat, idx) => (
                    <div key={idx} style={{ 
                        background: 'var(--bg-card)', 
                        border: '1px solid var(--border)', 
                        padding: '1rem', 
                        borderRadius: 'var(--radius)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, marginTop: '0.25rem', color: 'var(--text-primary)' }}>{stat.value}</div>
                        {stat.change && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.5rem', fontSize: '0.8rem', color: stat.changeType === 'positive' ? 'var(--success)' : 'var(--text-secondary)' }}>
                                <i className={stat.changeType === 'positive' ? "fa-solid fa-arrow-trend-up" : "fa-solid fa-circle"}></i>
                                <span>{stat.change}</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Mock Data Table Section */}
            <div style={{ 
                background: 'var(--bg-card)', 
                border: '1px solid var(--border)', 
                borderRadius: 'var(--radius)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                overflow: 'hidden'
            }}>
                <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-secondary)' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Active {title}</h3>
                    <button className="btn-primary" style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                        <i className="fa-solid fa-plus"></i> New Setup
                    </button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.02)' }}>
                                {Object.keys(mockData[0] || {}).map((col, idx) => (
                                    <th key={idx} style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{col.replace(/([A-Z])/g, ' $1')}</th>
                                ))}
                                <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mockData.map((row, rowIdx) => (
                                <tr key={rowIdx} style={{ borderBottom: rowIdx < mockData.length - 1 ? '1px solid var(--border)' : 'none', transition: 'var(--transition)' }} className="table-row-hover">
                                    {Object.values(row).map((val, colIdx) => (
                                        <td key={colIdx} style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem', color: colIdx === 0 ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: colIdx === 0 ? 600 : 400 }}>
                                            {typeof val === 'boolean' ? (
                                                <span style={{ 
                                                    padding: '2px 8px', 
                                                    borderRadius: '4px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: 600, 
                                                    background: val ? 'rgba(5, 150, 105, 0.1)' : 'rgba(107, 114, 128, 0.1)', 
                                                    color: val ? 'var(--success)' : 'var(--text-secondary)' 
                                                }}>{val ? 'Active' : 'Draft'}</span>
                                            ) : val}
                                        </td>
                                    ))}
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                                        <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '0.8rem', marginRight: '0.5rem' }}>Edit</button>
                                        <button className="family-menu-trigger" style={{ padding: '4px 8px' }}><i className="fa-solid fa-ellipsis"></i></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
